import { isRecord } from '@/helpers/isRecord'
import {
    NativeBridge,
    NotificationPermissionStatus,
    NotificationTappedPayload,
} from '@/types'

import { isNotificationTappedPayload } from './isNotificationTappedPayload'

declare global {
    interface Window {
        webkit?: {
            messageHandlers?: {
                nativeBridge?: { postMessage: (message: string) => void }
            }
        }
        // Registered by the native Android WebView via addJavascriptInterface(..., "NativeBridge").
        NativeBridge?: { postMessage: (message: string) => void }
        // Called by native code (both platforms) to deliver a response or event to the web app -
        // see the contract note on createNativeAppWebViewBridge below.
        __nativeBridgeReceive?: (message: string) => void
    }
}

type BridgeRequestMethod = 'getNotificationPermissionStatus' | 'requestNotificationPermission' | 'getPushToken'

interface BridgeResponseMessage {
    type: 'response'
    id: string
    payload?: unknown
    error?: string
}

interface BridgeEventMessage {
    type: 'event'
    name: string
    payload?: unknown
}

type BridgeIncomingMessage = BridgeResponseMessage | BridgeEventMessage

const REQUEST_TIMEOUT_MS = 10000

const isBridgeIncomingMessage = (value: unknown): value is BridgeIncomingMessage => (
    isRecord(value) && (value.type === 'response' || value.type === 'event')
)

const isNotificationPermissionStatus = (value: unknown): value is NotificationPermissionStatus => (
    value === 'granted' || value === 'denied' || value === 'default'
)

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string'

const parseIncomingData = (data: unknown): BridgeIncomingMessage | null => {
    let parsed: unknown = data

    if (typeof data === 'string') {
        try {
            parsed = JSON.parse(data)
        } catch {
            return null
        }
    }

    return isBridgeIncomingMessage(parsed) ? parsed : null
}

const getOutgoingChannel = (): ((message: string) => void) | null => {
    if (window.webkit?.messageHandlers?.nativeBridge) {
        return message => window.webkit?.messageHandlers?.nativeBridge?.postMessage(message)
    }

    if (window.NativeBridge) {
        return message => window.NativeBridge?.postMessage(message)
    }

    return null
}

/**
 * Ready-to-go NativeBridge implementation for a native iOS/Android app shell (not a React Native
 * WebView - see NativeBridgeContext for how to swap this out if that assumption turns out wrong).
 *
 * Contract the native side needs to implement:
 * - iOS (WKWebView): register a script message handler named "nativeBridge"
 *   (`userContentController.add(self, name: "nativeBridge")`), so
 *   `window.webkit.messageHandlers.nativeBridge.postMessage(<jsonString>)` reaches native code.
 * - Android (WebView): register a JS interface named "NativeBridge" exposing a
 *   `@JavascriptInterface fun postMessage(message: String)` method
 *   (`webView.addJavascriptInterface(NativeBridgeInterface(), "NativeBridge")`), so
 *   `window.NativeBridge.postMessage(<jsonString>)` reaches native code.
 * - Both platforms send messages back to the web app the same way: call
 *   `window.__nativeBridgeReceive(<jsonString>)` (e.g. via `webView.evaluateJavaScript(...)` /
 *   `webView.evaluateJavascript(...)`, on the main/UI thread), for both request responses and
 *   unsolicited events (e.g. a tapped push notification).
 *
 * Messages are JSON envelopes: outgoing `{ type: 'request', id, method }`; incoming
 * `{ type: 'response', id, payload?, error? }` or `{ type: 'event', name, payload? }`, responses
 * correlated to their request by `id`.
 */
export const createNativeAppWebViewBridge = (): NativeBridge => {
    const pendingRequests = new Map<string, { resolve: (value: unknown) => void, reject: (error: Error) => void }>()
    const tappedHandlers = new Set<(payload: NotificationTappedPayload) => void>()
    const sendToNative = getOutgoingChannel()

    window.__nativeBridgeReceive = (raw: string): void => {
        const message = parseIncomingData(raw)

        if (! message) {
            return
        }

        if (message.type === 'response') {
            const pending = pendingRequests.get(message.id)

            if (! pending) {
                return
            }

            pendingRequests.delete(message.id)

            if (message.error) {
                pending.reject(new Error(message.error))
            } else {
                pending.resolve(message.payload)
            }

            return
        }

        if (message.name === 'notificationTapped') {
            const { payload } = message

            if (isNotificationTappedPayload(payload)) {
                tappedHandlers.forEach(handler => handler(payload))
            }
        }
    }

    const sendRequest = <T, >(method: BridgeRequestMethod, isResponse: (value: unknown) => value is T): Promise<T> => {
        if (! sendToNative) {
            return Promise.reject(new Error('Not running inside the native app webview'))
        }

        const id = `${method}-${Date.now()}-${Math.random().toString(36).slice(2)}`

        return new Promise<T>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                pendingRequests.delete(id)
                reject(new Error(`Native bridge request "${method}" timed out`))
            }, REQUEST_TIMEOUT_MS)

            pendingRequests.set(id, {
                resolve: (value): void => {
                    clearTimeout(timeoutId)

                    if (isResponse(value)) {
                        resolve(value)
                    } else {
                        reject(new Error(`Native bridge request "${method}" returned an unexpected response`))
                    }
                },
                reject: (error): void => {
                    clearTimeout(timeoutId)
                    reject(error)
                },
            })

            sendToNative(JSON.stringify({ type: 'request', id, method }))
        })
    }

    return {
        isAvailable: !! sendToNative,
        getNotificationPermissionStatus: () => sendRequest('getNotificationPermissionStatus', isNotificationPermissionStatus),
        requestNotificationPermission: () => sendRequest('requestNotificationPermission', isNotificationPermissionStatus),
        getPushToken: () => sendRequest('getPushToken', isNullableString),
        onNotificationTapped: (handler) => {
            tappedHandlers.add(handler)

            return () => {
                tappedHandlers.delete(handler)
            }
        },
    }
}
